import { useEffect } from 'react'
import { wsMessageSchema, type WsMessage } from '@familynest/shared'
import { API_BASE, isNative, getToken } from './config'

/**
 * WebSocket-Notify-Client (ADR-004): eine Verbindung, dispatcht nach Kanal.
 * Der Server sendet nur Hinweise ({channel}); die Komponenten laden das Delta
 * per REST nach. Reconnect mit Backoff.
 *
 * Auth (ADR-042): Web per Session-Cookie (same-origin). Nativer Build kann keine
 * Header/Cookies über die WebSocket-API senden → First-Message-Auth mit dem
 * Bearer-Token (`{type:'auth',token}`) direkt nach `open`.
 */
type Handler = () => void
const handlers = new Map<WsMessage['channel'], Set<Handler>>()
let socket: WebSocket | null = null
let reconnectDelay = 1000

/** WS-Basis aus API_BASE (nativ) bzw. dem aktuellen Origin (Web) ableiten. */
function wsUrl(): string {
  const base = API_BASE || location.origin
  return `${base.replace(/^http/, 'ws')}/ws`
}

export function connectWs(): void {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return
  socket = new WebSocket(wsUrl())
  socket.onopen = () => {
    reconnectDelay = 1000
    // Nativer Client: sofort per First-Message authentifizieren.
    if (isNative) {
      const token = getToken()
      if (token) socket?.send(JSON.stringify({ type: 'auth', token }))
    }
  }
  socket.onmessage = (e) => {
    try {
      const msg = wsMessageSchema.parse(JSON.parse(e.data as string))
      handlers.get(msg.channel)?.forEach((h) => h())
    } catch {
      /* ignorieren */
    }
  }
  socket.onclose = () => {
    socket = null
    setTimeout(connectWs, reconnectDelay)
    reconnectDelay = Math.min(reconnectDelay * 2, 15000)
  }
}

export function onChannel(channel: WsMessage['channel'], h: Handler): () => void {
  const set = handlers.get(channel) ?? new Set()
  set.add(h)
  handlers.set(channel, set)
  return () => set.delete(h)
}

/** React-Hook: bei Live-Hinweis auf einem Kanal callback ausführen. */
export function useLive(channel: WsMessage['channel'], cb: Handler): void {
  useEffect(() => onChannel(channel, cb), [channel, cb])
}
